(() => {
  'use strict';

  const colors = [
    { key: 'bone-white', label: 'Bone White', sku: 'YAF-L-07-SHADE', material: 'PLA Matte · Bone White' },
    { key: 'natural-amber', label: 'Oranje', sku: 'YAF-L-07-NATURAL', material: 'PLA Matte · Mandarin Orange (11300)' }
  ];
  const naturalStudio = 'lighting-07-natural-studio-lit-v1.webp';
  const naturalImages = [1, 2, 3, 4, 5].map(number => `lighting-07-natural-life-${String(number).padStart(2, '0')}-v1.webp`).concat(naturalStudio);

  function escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  }

  function stem(filename) {
    let basename = String(filename || '').split(/[?#]/)[0].split('/').pop();
    try { basename = decodeURIComponent(basename); } catch {}
    return basename.replace(/\.[^.]+$/, '').replace(/_(?:[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[a-f0-9]{32}|\d+)$/i, '');
  }

  function isNaturalMedia(filename) {
    return /^lighting-07-natural-/i.test(stem(filename));
  }

  function previewMode(settings) {
    return settings.preview === true || (settings.preview !== false && (settings.config || settings).mode !== 'shopify');
  }

  function nativeId(value) {
    const identifier = String(value ?? '');
    return /^[1-9]\d*$/.test(identifier) ? identifier : '';
  }

  function price(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function colorLabel(settings, choice) {
    return choice.key === 'natural-amber' && settings.locale === 'de' ? 'Orange' : choice.label;
  }

  function apply(settings, product, choice) {
    if (!choice) return product;
    const media = product.colorMedia[choice.key];
    const kitPrice = (settings.config || settings).kit?.price ?? product.colorKitPrice;
    return {
      ...product,
      colorKey: choice.key,
      colorLabel: colorLabel(settings, choice),
      colorMaterial: choice.material,
      sku: choice.sku,
      variantId: choice.variantId,
      available: choice.available,
      shadePrice: choice.price,
      price: choice.price + kitPrice,
      material: choice.material,
      images: media.images.slice(),
      studioImage: media.studioImage,
      lifestyleImages: media.lifestyleImages.slice(),
      hoverImage: media.hoverImage,
      imageCaptions: { ...media.imageCaptions }
    };
  }

  function prepare(settings, product) {
    if (product.id !== '07') return product;
    if (product.colorChoices && product.colorMedia) return select(settings, product, settings.variantId || product.colorKey);
    const config = settings.config || settings;
    const preview = previewMode(settings);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const choices = colors.flatMap(color => {
      let variant = variants.find(candidate => String(candidate.sku || '').toUpperCase() === color.sku);
      if (!variant && color.key === 'bone-white' && nativeId(product.variantId)) {
        const matching = variants.find(candidate => nativeId(candidate.id) === nativeId(product.variantId));
        if (!matching || String(matching.sku || '').toUpperCase() === color.sku) {
          variant = matching || { id: product.variantId, price: product.shadePrice, available: product.available, sku: color.sku };
        }
      }
      if (variant && nativeId(variant.id) && price(variant.price)) {
        return [{ ...color, variantId: nativeId(variant.id), price: variant.price, available: variant.available === true, native: true }];
      }
      if (!preview || !price(product.shadePrice)) return [];
      return [{ ...color, variantId: `preview-07-${color.key}`, price: product.shadePrice, available: true, native: false }];
    });
    if (!choices.length) return product;
    const allMedia = [...(product.images || []), ...variants.map(variant => variant.image).filter(image => typeof image === 'string')];
    const resolvedNatural = naturalImages.map(filename => allMedia.find(candidate => stem(candidate) === stem(filename)) || filename);
    const whiteImages = (product.images || []).filter(filename => !isNaturalMedia(filename));
    const whiteStudio = isNaturalMedia(product.studioImage) ? whiteImages.at(-1) : product.studioImage;
    const whiteLifestyle = whiteImages.filter(filename => stem(filename) !== stem(whiteStudio));
    const naturalLifestyle = resolvedNatural.slice(0, -1);
    const prepared = {
      ...product,
      colorChoices: choices,
      colorKitPrice: config.kit?.price ?? product.price - product.shadePrice,
      colorMedia: {
        'bone-white': {
          images: whiteLifestyle.concat(whiteStudio || []).filter(Boolean), studioImage: whiteStudio,
          lifestyleImages: whiteLifestyle,
          hoverImage: whiteLifestyle.includes(product.hoverImage) ? product.hoverImage : whiteLifestyle[0],
          imageCaptions: product.imageCaptions || {}
        },
        'natural-amber': {
          images: resolvedNatural, studioImage: resolvedNatural.at(-1),
          lifestyleImages: naturalLifestyle, hoverImage: naturalLifestyle[0], imageCaptions: {}
        }
      }
    };
    return select(settings, prepared, settings.variantId || 'natural-amber');
  }

  function select(settings, product, selection) {
    if (product.id !== '07' || !product.colorChoices?.length || !product.colorMedia) return product;
    const choice = product.colorChoices.find(candidate => candidate.key === selection || String(candidate.variantId) === String(selection))
      || product.colorChoices.find(candidate => candidate.key === product.colorKey)
      || product.colorChoices[0];
    return apply(settings, product, choice);
  }

  function choices(settings, product) {
    if (product.id !== '07' || !product.colorChoices || product.colorChoices.length < 2) return '';
    const german = settings.locale === 'de';
    const title = german ? 'Deine Ausführung' : 'Jouw uitvoering';
    const note = product.colorKey === 'natural-amber'
      ? german ? 'Ein orangefarbener Schirm mit matter Oberfläche. Mit eingeschaltetem Licht entsteht ein intensiver, warmer Orangeton.' : 'Een oranje kap met een matte afwerking. Met het licht aan ontstaat een intense, warme oranje gloed.'
      : german ? 'Ein weicher, heller Farbton mit matter Oberfläche.' : 'Een zachte, lichte tint met een matte afwerking.';
    return `<fieldset class="yv-colors"><legend>${title} <span>${escape(product.colorLabel)}</span></legend><div class="yv-color-options">${product.colorChoices.map(choice => `<button type="button" class="yv-color-button" data-lighting-variant="${escape(choice.key)}" data-variant-model="07" aria-pressed="${choice.key === product.colorKey}"><span class="yv-color-swatch" data-color="${escape(choice.key)}" aria-hidden="true"></span><span class="yv-color-label">${escape(colorLabel(settings, choice))}</span><small class="yv-color-material">${escape(choice.material)}</small>${choice.available ? '' : `<small class="yv-color-availability">${german ? 'Zurzeit nicht verfügbar' : 'Tijdelijk niet beschikbaar'}</small>`}</button>`).join('')}</div><p class="yv-color-note" aria-live="polite">${note}</p></fieldset>`;
  }

  function forCart(settings, product, item) {
    if (!product?.colorChoices) return product;
    const choice = product.colorChoices.find(candidate => String(candidate.variantId) === String(item.variant_id));
    return choice ? apply(settings, product, choice) : product;
  }

  window.YaficoVariants = { prepare, select, choices, forCart, isNaturalMedia };
})();
